import React, { useEffect, useState } from 'react';
import api from '../services/api';

export default function PublishSlots({ practiceId }) {
  const [providers, setProviders] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsError, setReasonsError] = useState(null);
  const [providerId, setProviderId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [apptTypeId, setApptTypeId] = useState('');
  const [reasonId, setReasonId] = useState('');
  const [slotDate, setSlotDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('09:00');
  const [count, setCount] = useState(1);
  const [duration, setDuration] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [successSlots, setSuccessSlots] = useState(null);
  const [failedSlots, setFailedSlots] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!practiceId) return;
    (async () => {
      try {
        const [p, d, a] = await Promise.all([
          api.request(`/practices/${practiceId}/voice/providers`).catch(() => ({ data: [] })),
          api.request(`/practices/${practiceId}/voice/departments`).catch(() => ({ data: [] })),
          api.request(`/practices/${practiceId}/ehr/appointment-types`).catch(() => ({ data: [] })),
        ]);
        setProviders(p.data || []);
        setDepartments(d.data || []);
        setApptTypes(a.data || []);
      } catch {}
    })();
  }, [practiceId]);

  // Load reason codes live from athena when provider + department are both selected.
  // Athena validates the (reason, provider, department) tuple at push time, so this
  // combo-scoped call is the only reliable source — practice-wide caches lie.
  useEffect(() => {
    if (!practiceId || !providerId || !departmentId) {
      setReasons([]);
      setReasonId('');
      return;
    }
    const provider = providers.find(p => String(p.id) === String(providerId));
    const dept = departments.find(d => String(d.id) === String(departmentId));
    if (!provider?.athena_provider_id || !dept?.athena_department_id) {
      setReasons([]);
      setReasonId('');
      return;
    }
    setReasonsLoading(true);
    setReasonsError(null);
    api.request(`/practices/${practiceId}/ehr/reason-codes/live?providerId=${provider.athena_provider_id}&departmentId=${dept.athena_department_id}`)
      .then(res => {
        setReasons(res.data || []);
        setReasonId('');
      })
      .catch(err => {
        setReasons([]);
        setReasonsError(err.message || 'Failed to load reason codes from athena.');
      })
      .finally(() => setReasonsLoading(false));
  }, [practiceId, providerId, departmentId, providers, departments]);

  const provider = providers.find(p => String(p.id) === String(providerId));
  const dept = departments.find(d => String(d.id) === String(departmentId));
  const canSubmit = providerId && departmentId && apptTypeId && reasonId
    && provider?.athena_provider_id && dept?.athena_department_id && !submitting;

  const handleSubmit = async () => {
    setError(null);
    setSuccessSlots(null);
    setFailedSlots(null);
    if (!providerId || !departmentId) {
      setError('Provider and Department are required.');
      return;
    }
    if (!apptTypeId) {
      setError('Appointment Type is required. Athena rejects slots without a valid type.');
      return;
    }
    if (!reasonId) {
      setError('Appointment Reason is required. Pick from the list athena returned for the selected provider + department.');
      return;
    }
    if (!provider?.athena_provider_id || !dept?.athena_department_id) {
      setError('Selected provider or department is missing athena IDs. Sync EHR first.');
      return;
    }

    const [h, m] = startTime.split(':').map(Number);
    const slots = [];
    for (let i = 0; i < count; i++) {
      const dt = new Date(`${slotDate}T00:00:00Z`);
      dt.setUTCHours(h, m + i * duration, 0, 0);
      slots.push({
        DateTime: dt.toISOString().replace('T', ' ').replace('.000Z', 'Z'),
        Duration: duration,
        AppointmentTypeID: parseInt(apptTypeId, 10),
        PatientAppointmentReasonID: parseInt(reasonId, 10),
        Provider: {
          ID: String(provider.athena_provider_id),
          IDType: 'EHRID',
          FirstName: provider.first_name || '',
          LastName: provider.last_name || ''
        },
        Location: { Department: String(dept.athena_department_id) }
      });
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/practices/${practiceId}/ehr/push-slots`, { slots });
      // 2xx — but still scan Slots[] for partial failures.
      const returnedSlots = res?.data?.Slots || res?.Slots || [];
      const successes = returnedSlots.filter(s => s?.ID || s?.statuscode === 200 || (!s?.statuscode && !s?.error));
      const failures = returnedSlots.filter(s => (s?.statuscode && s.statuscode !== 200) || s?.error);
      setSuccessSlots(successes.length > 0 || returnedSlots.length === 0 ? (successes.length ? successes : [{ note: 'Published (no per-slot detail returned)' }]) : null);
      setFailedSlots(failures.length > 0 ? failures : null);
      if (failures.length === count) {
        setError('All slots failed — see details below.');
      }
    } catch (err) {
      if (err?.body?.code === 'ATHENA_SCOPE_NOT_APPROVED') {
        setError('Publishing slots requires Athena Go-Live approval for this practice.');
      } else {
        setError(err?.body?.error || err?.message || 'Publish failed.');
      }
      // Bridge now returns per-slot failures in err.body.slots — render them.
      if (Array.isArray(err?.body?.slots) && err.body.slots.length > 0) {
        setFailedSlots(err.body.slots);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 760 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Publish Open Slots</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
        Push open appointment slots to athena for self-scheduling. Slots are created for the selected provider + department starting at the chosen time.
      </p>

      <div style={styles.card}>
        <div style={styles.grid}>
          <Field label="Provider *">
            <select style={styles.input} value={providerId} onChange={e => setProviderId(e.target.value)}>
              <option value="">Select provider…</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Department *">
            <select style={styles.input} value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
              <option value="">Select department…</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name || d.department_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Appointment Type *">
            <select style={styles.input} value={apptTypeId} onChange={e => setApptTypeId(e.target.value)}>
              <option value="">Select type…</option>
              {apptTypes.map(t => (
                <option key={t.id || t.athena_appointment_type_id} value={t.athena_appointment_type_id || t.id}>
                  {t.name || t.appointment_type_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Appointment Reason *">
            <select
              style={styles.input}
              value={reasonId}
              onChange={e => setReasonId(e.target.value)}
              disabled={!providerId || !departmentId || reasonsLoading}
            >
              <option value="">
                {!providerId || !departmentId
                  ? 'Select provider + department first…'
                  : reasonsLoading
                    ? 'Loading reasons from athena…'
                    : reasonsError
                      ? 'Failed to load reasons'
                      : reasons.length === 0
                        ? 'No reasons available for this combo'
                        : 'Select reason…'}
              </option>
              {reasons.map(r => (
                <option key={r.id} value={r.id}>{r.text} (ID {r.id})</option>
              ))}
            </select>
            {reasonsError && <span style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>{reasonsError}</span>}
          </Field>
          <Field label="Date">
            <input type="date" style={styles.input} value={slotDate} onChange={e => setSlotDate(e.target.value)} />
          </Field>
          <Field label="Start Time">
            <input type="time" style={styles.input} value={startTime} onChange={e => setStartTime(e.target.value)} />
          </Field>
          <Field label="Slot Count">
            <input type="number" min="1" max="20" style={styles.input} value={count} onChange={e => setCount(parseInt(e.target.value, 10) || 1)} />
          </Field>
          <Field label="Duration (min)">
            <input type="number" min="5" max="240" step="5" style={styles.input} value={duration} onChange={e => setDuration(parseInt(e.target.value, 10) || 30)} />
          </Field>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button onClick={handleSubmit} disabled={!canSubmit} style={{ ...styles.primaryBtn, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
            {submitting ? 'Publishing…' : `Publish ${count} slot${count === 1 ? '' : 's'}`}
          </button>
          {error && <span style={{ color: '#dc2626', fontSize: 13 }}>{error}</span>}
        </div>

        {successSlots && successSlots.length > 0 && (
          <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 12 }}>
            <strong style={{ color: '#166534' }}>Published {successSlots.length} slot{successSlots.length === 1 ? '' : 's'}.</strong>
            {successSlots.some(s => s?.ID) && (
              <ul style={{ margin: '6px 0 0 16px', padding: 0, color: '#374151', fontSize: 11 }}>
                {successSlots.filter(s => s?.ID).map((s, i) => (
                  <li key={i}>athena slot ID: <code>{s.ID}</code>{s.DateTime ? ` — ${s.DateTime}` : ''}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {failedSlots && failedSlots.length > 0 && (
          <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12 }}>
            <strong style={{ color: '#991b1b' }}>Athena rejected {failedSlots.length} slot{failedSlots.length === 1 ? '' : 's'}:</strong>
            <ul style={{ margin: '6px 0 0 16px', padding: 0, color: '#374151', fontSize: 11, lineHeight: 1.5 }}>
              {failedSlots.map((s, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  <div><strong>Error:</strong> {s.detailedmessage || s.error || 'Unknown failure'}</div>
                  {s.requested && (
                    <div style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 10 }}>
                      {Object.entries(s.requested).map(([k, v]) => `${k}=${v}`).join(' · ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      {children}
    </label>
  );
}

const styles = {
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  input: { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 },
  primaryBtn: {
    padding: '8px 18px', border: 'none', borderRadius: 7, background: '#2563eb',
    color: '#fff', fontWeight: 600, fontSize: 13,
  }
};
